import styled from "@emotion/styled";
import { isEqual } from "lodash";
import { FC, ReactNode } from "react";
import { FieldInputProps } from "react-final-form";
import { useSelector } from "react-redux";

import { Error } from "@livechat/design-system-icons";
import { Icon, Tag, Text } from "@livechat/design-system-react-components";

import { NOT_DETECTED_KEY } from "components/filters/language-filter/helpers";
import PriorityIndicator from "components/priority/PriorityIndicator";
import { ratingToColorMap, ratingToIconMap, ratingToTextMap } from "constants/rating";
import { TICKET_PRIORITY_LABEL_MAP } from "constants/tickets/priorities";
import { ticketDetailedSourcesLabel } from "helpers/tickets";
import { isEmail } from "helpers/validate";
import { Rating, TicketPriority, TicketStatus, TicketStatusFormattedValues } from "interfaces/entities/ticket";
import { AccordionAgentTag } from "routes/automations/shared/components/AccordionAgentTag";
import { AccordionTeamTag } from "routes/automations/shared/components/AccordionTeamTag";
import { selectAgents } from "store/entities/agents";
import { selectLanguageLabels } from "store/entities/application-settings/selectors";
import { selectLicenseId } from "store/entities/license";
import { selectMailboxes } from "store/entities/mailboxes/selectors";
import {
  IRuleTriggerAgentChanged,
  IRuleTriggerAgentID,
  IRuleTriggerDetailedSourceType,
  IRuleTriggerEventCount,
  IRuleTriggerFollowerAdded,
  IRuleTriggerFollowerID,
  IRuleTriggerFollowerRemoved,
  IRuleTriggerHasAssignee,
  IRuleTriggerIsNew,
  IRuleTriggerIsSpam,
  IRuleTriggerLanguage,
  IRuleTriggerNewAgentMessage,
  IRuleTriggerNewMessage,
  IRuleTriggerPriority,
  IRuleTriggerPriorityChanged,
  IRuleTriggerRating,
  IRuleTriggerRatingChanged,
  IRuleTriggerRequesterEmail,
  IRuleTriggerSecondsPassedSinceEvent,
  IRuleTriggerSourceMailbox,
  IRuleTriggerStatus,
  IRuleTriggerStatusChanged,
  IRuleTriggerSubject,
  IRuleTriggerTagAdded,
  IRuleTriggerTagID,
  IRuleTriggerTagRemoved,
  IRuleTriggerTeamChanged,
  IRuleTriggerTeamID,
} from "store/entities/rules/interfaces";
import { selectTags } from "store/entities/tags";
import { selectTeams } from "store/entities/teams";
import { plural } from "utils/string";

import { TriggerFieldArrayElement } from "../../interfaces";
import { guessTimeUnit, timeUnitLabel } from "../editor/input";
import { TIME_CONDITION_EVENTS } from "../editor/utils";

import { TRIGGER_ANY_AGENT_OPTION_KEY, TRIGGER_UNASSIGNED_OPTION_KEY, isTriggerType } from "./utils";

interface IProps {
  input: FieldInputProps<TriggerFieldArrayElement>;
}
export const TriggerLabel: FC<IProps> = ({ input }) => {
  const tagsById = useSelector(selectTags);
  const teamsById = useSelector(selectTeams);
  const agentsById = useSelector(selectAgents);
  const mailboxesById = useSelector(selectMailboxes);
  const licenseId = useSelector(selectLicenseId);
  const detectedLanguages = useSelector(selectLanguageLabels);

  if (!input.value.type) {
    return null;
  }

  if (isTriggerType<IRuleTriggerIsNew>(input, "isNew")) {
    return <Label>{input.value.eq ? "Ticket created" : "Ticket changed"}</Label>;
  }

  if (isTriggerType<IRuleTriggerDetailedSourceType>(input, "detailedSourceType")) {
    return (
      <>
        <Label>Source</Label>
        <Tag>{input.value.invert ? "is not" : "is"}</Tag>
        <Colon />
        {input.value.in.length > 0 ? (
          input.value.in.map(source => <Tag key={source}>{ticketDetailedSourcesLabel[source]}</Tag>)
        ) : (
          <InvalidTag label="Select source…" />
        )}
      </>
    );
  }

  if (isTriggerType<IRuleTriggerStatus>(input, "status")) {
    return (
      <>
        <Label>Status</Label>
        <Tag>{input.value.invert ? "is not" : "is"}</Tag>
        <Colon />
        {input.value.in.length > 0 ? (
          input.value.in.map(status => <Tag key={status}>{TicketStatusFormattedValues[status]}</Tag>)
        ) : (
          <InvalidTag label="Select status…" />
        )}
      </>
    );
  }

  if (isTriggerType<IRuleTriggerStatusChanged>(input, "statusChanged")) {
    const { oldStatusIn, newStatusIn } = input.value;

    const shouldDisplayAny = (statuses: TicketStatus[] | undefined): boolean => statuses === undefined;

    const shouldDisplayInvalid = (statuses: TicketStatus[] | undefined): boolean => statuses?.length === 0;

    const displayStatuses = (statuses: TicketStatus[] | undefined): ReactNode[] | null => {
      if (!statuses) {
        return null;
      }

      return statuses.map(status => <Tag key={status}>{TicketStatusFormattedValues[status]}</Tag>);
    };

    const InvalidStatusTag = (): ReactNode => {
      return <InvalidTag label="Select status…" />;
    };

    const AnyStatusTag = (): ReactNode => {
      return <Tag>any status</Tag>;
    };

    return (
      <>
        <Label>Status changed from</Label>
        {shouldDisplayAny(oldStatusIn) && <AnyStatusTag />}
        {shouldDisplayInvalid(oldStatusIn) && <InvalidStatusTag />}
        {displayStatuses(oldStatusIn)}
        <Label>to</Label>
        {shouldDisplayAny(newStatusIn) && <AnyStatusTag />}
        {shouldDisplayInvalid(newStatusIn) && <InvalidStatusTag />}
        {displayStatuses(newStatusIn)}
      </>
    );
  }

  if (isTriggerType<IRuleTriggerPriority>(input, "priority")) {
    return (
      <>
        <Label>Priority</Label>
        <Tag>{input.value.invert ? "is not" : "is"}</Tag>
        <Colon />
        {input.value.in.length > 0 ? (
          input.value.in.map(priority => (
            <Tag key={priority} leftNode={<PriorityIndicator priority={priority} />}>
              {TICKET_PRIORITY_LABEL_MAP[priority]}
            </Tag>
          ))
        ) : (
          <InvalidTag label="Select priority…" />
        )}
      </>
    );
  }

  if (isTriggerType<IRuleTriggerPriorityChanged>(input, "priorityChanged")) {
    const { oldPriorityIn, newPriorityIn } = input.value;

    const shouldDisplayAny = (statuses: TicketPriority[] | undefined): boolean => statuses === undefined;

    const shouldDisplayInvalid = (statuses: TicketPriority[] | undefined): boolean => statuses?.length === 0;

    const displayStatuses = (statuses: TicketPriority[] | undefined): ReactNode[] | null => {
      if (!statuses) {
        return null;
      }

      return statuses.map(status => (
        <Tag key={status} leftNode={<PriorityIndicator priority={status} />}>
          {TICKET_PRIORITY_LABEL_MAP[status]}
        </Tag>
      ));
    };

    const InvalidPriorityTag = (): ReactNode => {
      return <InvalidTag label="Select priority…" />;
    };

    const AnyPriorityTag = (): ReactNode => {
      return <Tag>any priority</Tag>;
    };

    return (
      <>
        <Label>Priority changed from</Label>
        {shouldDisplayAny(oldPriorityIn) && <AnyPriorityTag />}
        {shouldDisplayInvalid(oldPriorityIn) && <InvalidPriorityTag />}
        {displayStatuses(oldPriorityIn)}
        <Label>to</Label>
        {shouldDisplayAny(newPriorityIn) && <AnyPriorityTag />}
        {shouldDisplayInvalid(newPriorityIn) && <InvalidPriorityTag />}
        {displayStatuses(newPriorityIn)}
      </>
    );
  }

  if (
    isTriggerType<IRuleTriggerRating>(input, "rating") ||
    isTriggerType<IRuleTriggerRatingChanged>(input, "ratingChanged")
  ) {
    const RatingTag = ({ rating }: { rating: Rating }): ReactNode => (
      <Tag key={rating} leftNode={<Icon source={ratingToIconMap[rating]} customColor={ratingToColorMap[rating]} />}>
        {ratingToTextMap[rating]}
      </Tag>
    );

    return (
      <>
        <Label>Rating</Label>
        <Tag>{input.value.type === "rating" ? "is" : "has been added"}</Tag>
        <Colon />
        {input.value.type === "rating" ? (
          input.value.in.length > 0 ? (
            input.value.in.map(rating => <RatingTag key={rating} rating={rating} />)
          ) : (
            <InvalidTag label="Select rating…" />
          )
        ) : input.value.newRatingIn.length > 0 ? (
          input.value.newRatingIn.map(rating => <RatingTag key={rating} rating={rating} />)
        ) : (
          <InvalidTag label="Select rating…" />
        )}
      </>
    );
  }

  if (
    isTriggerType<IRuleTriggerSubject>(input, "subject") ||
    isTriggerType<IRuleTriggerNewMessage>(input, "newMessage") ||
    isTriggerType<IRuleTriggerNewAgentMessage>(input, "newAgentMessage")
  ) {
    const { containsInCI, containsIn, containsWholePhraseInCI, containsWholePhraseIn, invert } = input.value;

    const getContainsText = (): string | null => {
      if (containsWholePhraseIn) {
        return "phrase (case-sensitive)";
      }

      if (containsWholePhraseInCI) {
        return "phrase";
      }

      if (containsInCI) {
        return "string of characters";
      }

      if (containsIn) {
        return "string of characters (case-sensitive)";
      }

      return null;
    };

    const getHeaderText = (): string | null => {
      if (input.value.type === "subject") {
        return "Subject";
      }

      if (input.value.type === "newMessage") {
        return "New message from requester";
      }

      if (input.value.type === "newAgentMessage") {
        return input.value.isPrivate !== undefined
          ? input.value.isPrivate
            ? "New private note"
            : "New message to requester"
          : "Any agent message";
      }

      return "Message";
    };

    return (
      <>
        <Label>{getHeaderText()}</Label>
        <Tag>{invert ? "does not contain" : "contains"}</Tag>
        <Tag>{getContainsText()}</Tag>
        <Colon />
      </>
    );
  }

  if (
    isTriggerType<IRuleTriggerTagID>(input, "tagID") ||
    isTriggerType<IRuleTriggerTagAdded>(input, "tagAdded") ||
    isTriggerType<IRuleTriggerTagRemoved>(input, "tagRemoved")
  ) {
    return (
      <>
        <Label>Tag</Label>
        {input.value.type === "tagAdded" && <Tag>has been added</Tag>}
        {input.value.type === "tagRemoved" && <Tag>has been removed</Tag>}
        {input.value.type === "tagID" && <Tag>{input.value.invert ? "is not" : "is"}</Tag>}
        <Colon />
        {input.value.in.length > 0 ? (
          input.value.in.map(tagId => {
            const tag = tagsById[tagId];

            if (!tag) {
              return <InvalidTag key={tagId} label="Tag removed" />;
            }

            return <Tag key={tagId}>{tag.name}</Tag>;
          })
        ) : (
          <InvalidTag label="Select tag…" />
        )}
      </>
    );
  }

  if (isTriggerType<IRuleTriggerEventCount>(input, "eventCount")) {
    const { authorType, eventTypeIn, eq, gt, gte, lt, lte } = input.value;

    const eventCount = eq ?? gt ?? gte ?? lt ?? lte;

    const getEventTypeText = (eventType: string): string => {
      if (eventType === "message") {
        return "message sent";
      }

      if (eventType === "ratingRequestSent") {
        return "rating request sent";
      }

      if (eventType === "followUpSent") {
        return "follow-up sent";
      }

      return eventType;
    };

    const getAuthorTypeText = (): string => {
      if (authorType === "agent") {
        return "agent";
      }

      if (authorType === "client") {
        return "requester";
      }

      if (authorType === "system") {
        return "system";
      }

      if (authorType === "rule") {
        return "rule";
      }

      return "anyone";
    };

    const getCompareText = (): string | null => {
      if (eq !== undefined) {
        return "exactly";
      }

      if (gt !== undefined) {
        return "more than";
      }

      if (gte !== undefined) {
        return "more than or exactly";
      }

      if (lt !== undefined) {
        return "is less than";
      }

      if (lte !== undefined) {
        return "less than or exactly";
      }

      return null;
    };

    return (
      <>
        <Label>Performed events number of</Label>
        {eventTypeIn.length > 0 ? (
          eventTypeIn.map(eventType => <Tag key={eventType}>{getEventTypeText(eventType)}</Tag>)
        ) : (
          <InvalidTag label="Select event type…" />
        )}
        <Label>by</Label>
        <Tag>{getAuthorTypeText()}</Tag>
        <Label>is</Label>
        <Tag>{getCompareText()}</Tag>
        {eventCount !== undefined ? <Tag>{eventCount}</Tag> : <InvalidTag label="Select number…" />}
      </>
    );
  }

  if (isTriggerType<IRuleTriggerSourceMailbox>(input, "sourceMailbox")) {
    const getSourceMailboxText = (source: "default" | string): string => {
      if (source === "default") {
        return `${licenseId}@`;
      }

      return `${licenseId}-${mailboxesById[source].name}@`;
    };

    return (
      <>
        <Label>Forwarding address</Label>
        <Tag>{input.value.invert ? "is not" : "is"}</Tag>
        <Colon />
        {input.value.in.length > 0 ? (
          input.value.in.map(source => {
            if (source === "default") {
              return <Tag key={source}>{getSourceMailboxText(source)}</Tag>;
            }

            const mailbox = mailboxesById[source];

            if (!mailbox) {
              return <InvalidTag key={source} label="Forwarding address removed" />;
            }

            return <Tag key={source}>{getSourceMailboxText(source)}</Tag>;
          })
        ) : (
          <InvalidTag label="Select forwarding address…" />
        )}
      </>
    );
  }

  if (isTriggerType<IRuleTriggerTeamID>(input, "teamID")) {
    return (
      <>
        <Label>Team</Label>
        <Tag>{input.value.invert ? "is not" : "is"}</Tag>
        <Colon />
        {input.value.in.length > 0 ? (
          input.value.in.map(teamId => {
            const team = teamsById[teamId];

            if (!team) {
              return <InvalidTag key={teamId} label="Team removed" />;
            }

            return <AccordionTeamTag key={teamId} teamID={teamId} />;
          })
        ) : (
          <InvalidTag label="Select team…" />
        )}
      </>
    );
  }

  if (isTriggerType<IRuleTriggerTeamChanged>(input, "teamChanged")) {
    const { oldTeamIn, newTeamIn } = input.value;

    const shouldDisplayAny = (teamIds: string[] | undefined): boolean => teamIds === undefined;

    const shouldDisplayEmpty = (teamIds: string[] | undefined): boolean => teamIds?.length === 0;

    const EmptyTeamIdsTag = (): ReactNode => {
      return <InvalidTag label="Select team…" />;
    };

    const AnyTeamTag = (): ReactNode => {
      return <Tag>any team</Tag>;
    };

    const displayTeamIds = (teamIds: string[] | undefined): React.ReactNode[] | null => {
      if (!teamIds) {
        return null;
      }

      return teamIds.map(teamId => {
        const team = teamsById[teamId];

        if (!team) {
          return <InvalidTag key={teamId} label="Team removed" />;
        }

        return <AccordionTeamTag key={teamId} teamID={teamId} />;
      });
    };

    return (
      <>
        <Label>Team changed from</Label>
        {shouldDisplayAny(oldTeamIn) && <AnyTeamTag />}
        {shouldDisplayEmpty(oldTeamIn) && <EmptyTeamIdsTag />}
        {displayTeamIds(oldTeamIn)}
        <Label>to</Label>
        {shouldDisplayAny(newTeamIn) && <AnyTeamTag />}
        {shouldDisplayEmpty(newTeamIn) && <EmptyTeamIdsTag />}
        {displayTeamIds(newTeamIn)}
      </>
    );
  }

  if (isTriggerType<IRuleTriggerAgentID>(input, "agentID")) {
    const { invert } = input.value;

    return (
      <>
        <Label>Agent</Label>
        <Tag>{invert ? "is not" : "is"}</Tag>
        <Colon />
        {input.value.in.length > 0 ? (
          input.value.in.map(agentId => {
            const agent = agentsById[agentId];

            if (!agent) {
              return <InvalidTag key={agentId} label="Agent removed" />;
            }

            return (
              <AccordionAgentTag
                key={agentId}
                agentID={agentId}
                agentType="agent"
                randomAgent={false}
                roundRobin={false}
              />
            );
          })
        ) : (
          <InvalidTag label="Select agent…" />
        )}
      </>
    );
  }

  if (isTriggerType<IRuleTriggerHasAssignee>(input, "hasAssignee")) {
    const { eq } = input.value;

    return (
      <>
        <Label>Agent</Label>
        <Tag>{eq ? "is any" : "is none"}</Tag>
      </>
    );
  }

  if (isTriggerType<IRuleTriggerAgentChanged>(input, "agentChanged")) {
    const { oldAgentIn, newAgentIn } = input.value;

    const shouldDisplaySpecialTags = (agentIds: string[] | undefined): boolean => agentIds === undefined;

    const shouldDisplayEmpty = (agentIds: string[] | undefined): boolean => agentIds?.length === 0;

    const EmptyAgentIdsTag = (): ReactNode => {
      return <InvalidTag label="Select agent…" />;
    };

    const AnyAgentTag = (): ReactNode => {
      return <Tag>any agent</Tag>;
    };

    const UnassignedAgentTag = (): ReactNode => {
      return <Tag>unassigned</Tag>;
    };

    const displayAgentIds = (agentIds: string[] | undefined): React.ReactNode[] | null => {
      if (!agentIds) {
        return null;
      }

      return agentIds.map(agentId => {
        if (agentId === TRIGGER_UNASSIGNED_OPTION_KEY) {
          return <UnassignedAgentTag key={agentId} />;
        }

        if (agentId === TRIGGER_ANY_AGENT_OPTION_KEY) {
          return <AnyAgentTag key={agentId} />;
        }

        const agent = agentsById[agentId];

        if (!agent) {
          return <InvalidTag key={agentId} label="Agent removed" />;
        }

        return (
          <AccordionAgentTag key={agentId} agentID={agentId} agentType="agent" randomAgent={false} roundRobin={false} />
        );
      });
    };

    return (
      <>
        <Label>Agent changed from</Label>
        {shouldDisplaySpecialTags(oldAgentIn) && (
          <>
            <AnyAgentTag />
            <UnassignedAgentTag />
          </>
        )}
        {shouldDisplayEmpty(oldAgentIn) && <EmptyAgentIdsTag />}
        {displayAgentIds(oldAgentIn)}
        <Label>to</Label>
        {shouldDisplaySpecialTags(newAgentIn) && <AnyAgentTag />}
        {shouldDisplayEmpty(newAgentIn) && <EmptyAgentIdsTag />}
        {displayAgentIds(newAgentIn)}
      </>
    );
  }

  if (
    isTriggerType<IRuleTriggerFollowerID>(input, "followerID") ||
    isTriggerType<IRuleTriggerFollowerAdded>(input, "followerAdded") ||
    isTriggerType<IRuleTriggerFollowerRemoved>(input, "followerRemoved")
  ) {
    return (
      <>
        <Label>Follower</Label>
        {input.value.type === "followerID" && <Tag>{input.value.invert ? "is not" : "is"}</Tag>}
        {input.value.type === "followerAdded" && <Tag>has been added</Tag>}
        {input.value.type === "followerRemoved" && <Tag>has been removed</Tag>}
        <Colon />
        {input.value.in.length > 0 ? (
          input.value.in.map(followerId => {
            const follower = agentsById[followerId];

            if (!follower) {
              return <InvalidTag key={followerId} label="Follower removed" />;
            }

            return (
              <AccordionAgentTag
                key={followerId}
                agentID={followerId}
                agentType="follower"
                randomAgent={false}
                roundRobin={false}
              />
            );
          })
        ) : (
          <InvalidTag label="Select follower…" />
        )}
      </>
    );
  }

  if (isTriggerType<IRuleTriggerRequesterEmail>(input, "requesterEmail")) {
    const getContextText = (): string | null => {
      if (input.value.in) {
        return input.value.invert ? "is not one of" : "is one of";
      }

      if (input.value.containsIn) {
        return input.value.invert ? "does not contain" : "contains";
      }

      return null;
    };

    return (
      <>
        <Label>Requester email</Label>
        <Tag>{getContextText()}</Tag>
        <Colon />
        {input.value.in && (
          <>
            {input.value.in.length > 0 ? (
              input.value.in.map(email => {
                if (!isEmail(email)) {
                  return <InvalidTag key={email} label={email} />;
                }

                return <Tag key={email}>{email}</Tag>;
              })
            ) : (
              <InvalidTag label="Enter email…" />
            )}
          </>
        )}
        {input.value.containsIn && (
          <>
            {input.value.containsIn.length > 0 ? (
              input.value.containsIn.map(emailFragment => <Tag key={emailFragment}>{emailFragment}</Tag>)
            ) : (
              <InvalidTag label="Enter part of email…" />
            )}
          </>
        )}
      </>
    );
  }

  if (isTriggerType<IRuleTriggerIsSpam>(input, "isSpam")) {
    return (
      <>
        <Label>{input.value.eq ? "Ticket is spam" : "Ticket is not spam"}</Label>
      </>
    );
  }

  if (isTriggerType<IRuleTriggerSecondsPassedSinceEvent>(input, "secondsPassedSinceEvent")) {
    const { eq, eventTypeIn } = input.value;

    const eventTypeLabels = isEqual(TIME_CONDITION_EVENTS, eventTypeIn) ? ["any ticket activity"] : eventTypeIn;

    const timeUnit = guessTimeUnit(eq);
    const timeValue = eq / timeUnit;
    const timeLabel = timeUnitLabel(timeUnit);

    const getTimeLabel = (): string | undefined => {
      switch (timeLabel) {
        case "seconds":
          return plural(timeValue, "second", "seconds");
        case "minutes":
          return plural(timeValue, "minute", "minutes");
        case "hours":
          return plural(timeValue, "hour", "hours");
        case "days":
          return plural(timeValue, "day", "days");
      }
    };

    const getEventTypeLabel = (eventType: string): string => {
      switch (eventType) {
        case "status":
          return "status changed";
        case "message":
          return "message sent";
        case "assignment":
          return "assignment changed";
        case "followUpSent":
          return "follow-up sent";
        case "priority":
          return "priority changed";
        case "ticketCreation":
          return "ticket created";
        default:
          return eventType;
      }
    };

    return (
      <>
        <Label>Time since</Label>
        {eventTypeLabels.length > 0 ? (
          eventTypeLabels.map(eventType => <Tag key={eventType}>{getEventTypeLabel(eventType)}</Tag>)
        ) : (
          <InvalidTag label="Select event type…" />
        )}
        <Label>is</Label>
        <Tag>{timeValue}</Tag>
        <Tag>{getTimeLabel()}</Tag>
      </>
    );
  }

  if (isTriggerType<IRuleTriggerLanguage>(input, "language")) {
    return (
      <>
        <Label>Language</Label>
        <Tag>{input.value.invert ? "is not" : "is"}</Tag>
        <Colon />
        {input.value.in.length > 0 ? (
          input.value.in.map(languageCode => {
            if (languageCode === NOT_DETECTED_KEY) {
              return <Tag key={languageCode}>Not detected</Tag>;
            }

            const languageLabel = detectedLanguages[languageCode];

            return <Tag key={languageCode}>{languageLabel}</Tag>;
          })
        ) : (
          <InvalidTag label="Select language…" />
        )}
      </>
    );
  }
};

const Colon: FC = () => <Text bold>:</Text>;

const InvalidTag: FC<{ label: string }> = ({ label }) => (
  <Tag kind="error" leftNode={<Icon source={Error} />}>
    {label}
  </Tag>
);

const Label = styled(Text)`
  font-weight: 600;
  white-space: nowrap;
`;
